import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { ArrowLeft, FilePdf, Trash, Receipt, CheckCircle, Warning, FileText, ChartBar, CalendarBlank, FileXls, FileCsv, FileCode } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line, ReferenceLine, Legend } from "recharts";

const fmtBRL = (v) => (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";

const FONTE_COLORS = {
  "PNCP":       { bg: "#005DA8", desc: "Portal Nacional de Contratações Públicas" },
  "Comprasnet": { bg: "#1B7B34", desc: "Compras.gov.br — Sistema Federal" },
  "TCE-CE":     { bg: "#B8941F", desc: "Tribunal de Contas do Estado do Ceará" },
};

function FonteBadge({ fonte }) {
  const cfg = FONTE_COLORS[fonte] || { bg: "#6B7280", desc: "" };
  return (
    <span
      className="inline-flex items-center font-bold text-[10px] tracking-wide px-2 py-1 rounded text-white"
      style={{ backgroundColor: cfg.bg }}
      title={cfg.desc}
    >
      {fonte || "—"}
    </span>
  );
}

function StatusBadge({ pesq }) {
  const finalizada = (pesq?.estatisticas?.qtd || 0) > 0 && (pesq?.estatisticas?.preco_referencia || 0) > 0;
  return finalizada ? (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30">
      <CheckCircle size={14} weight="duotone" /> Finalizada
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30">
      <Warning size={14} weight="duotone" /> Em andamento
    </span>
  );
}

function urlComprovacao(item) {
  const ncc = (item.numero_controle_compra || "").trim();
  const numero = (item.numero || "").trim();
  const objeto = (item.descricao_item || item.objeto || "").trim();
  const enc = (s) => encodeURIComponent(s || "");
  if (item.fonte === "PNCP") {
    return ncc ? `https://pncp.gov.br/app/editais/${enc(ncc)}`
               : `https://pncp.gov.br/app/contratos?q=${enc(numero || objeto.slice(0, 80))}`;
  }
  if (item.fonte === "Comprasnet") {
    const cod = ncc.startsWith("CN-") ? ncc.substring(3).split("-")[0] : "";
    if (cod && /^\d+$/.test(cod)) {
      return `https://catalogo.compras.gov.br/cnbs-web/busca?numeroItemMaterial=${enc(cod)}`;
    }
    return `https://catalogo.compras.gov.br/cnbs-web/busca?q=${enc(objeto.slice(0, 80) || numero)}`;
  }
  if (item.fonte === "TCE-CE") {
    return `https://www.tce.ce.gov.br/cidadao/consulta-de-contratos?busca=${enc(numero || objeto.slice(0, 80))}`;
  }
  return null;
}

export default function PesquisaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("itens");
  const [filtro, setFiltro] = useState("");
  const [fonteFiltro, setFonteFiltro] = useState("");
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/pesquisas-precos/${id}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erro ao carregar pesquisa"))
      .finally(() => setLoading(false));
  }, [id]);

  const pesq = data?.pesquisa;
  const contract = data?.contract;
  const itens = useMemo(() => pesq?.itens || [], [pesq]);
  const st = pesq?.estatisticas || {};

  const fontesPresentes = useMemo(() => {
    const s = new Set();
    itens.forEach((it) => { if (it.fonte) s.add(it.fonte); });
    return Array.from(s);
  }, [itens]);

  const itensFiltrados = useMemo(() => {
    const f = (filtro || "").trim().toLowerCase();
    return itens.filter((it) => {
      if (fonteFiltro && it.fonte !== fonteFiltro) return false;
      if (!f) return true;
      return (
        (it.descricao_item || it.objeto || "").toLowerCase().includes(f) ||
        (it.fornecedor || "").toLowerCase().includes(f) ||
        (it.numero || "").toLowerCase().includes(f)
      );
    });
  }, [itens, filtro, fonteFiltro]);

  const baixarPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await fetch(`${API}/pesquisas-precos/${id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pesquisa_precos_${id.slice(0, 8)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF baixado");
    } catch { toast.error("Erro ao baixar PDF"); }
    finally { setDownloadingPdf(false); }
  };

  const excluir = async () => {
    if (!window.confirm("Excluir esta pesquisa? Esta ação não pode ser desfeita.")) return;
    try {
      await api.delete(`/pesquisas-precos/${id}`);
      toast.success("Pesquisa excluída");
      navigate("/pesquisas");
    } catch { toast.error("Erro ao excluir"); }
  };

  const exportar = async (formato) => {
    try {
      const res = await fetch(`${API}/pesquisas-precos/${id}/export?format=${formato}`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `pesquisa_${id.slice(0, 8)}.${formato}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Arquivo ${formato.toUpperCase()} baixado`);
    } catch { toast.error(`Erro ao exportar ${formato}`); }
  };

  // Distribuição de valores em buckets (histograma)
  const histogramData = useMemo(() => {
    const valores = itens.map((it) => parseFloat(it.valor || it.valor_unitario || 0)).filter((v) => v > 0);
    if (valores.length === 0) return [];
    const min = Math.min(...valores);
    const max = Math.max(...valores);
    if (max === min) return [{ faixa: `R$ ${min.toFixed(2)}`, qtd: valores.length }];
    const n_buckets = Math.min(8, Math.max(3, Math.ceil(Math.sqrt(valores.length))));
    const w = (max - min) / n_buckets;
    const buckets = Array.from({ length: n_buckets }, (_, i) => ({
      i, low: min + i * w, high: min + (i + 1) * w, qtd: 0,
    }));
    valores.forEach((v) => {
      let idx = Math.floor((v - min) / w);
      if (idx >= n_buckets) idx = n_buckets - 1;
      buckets[idx].qtd += 1;
    });
    return buckets.map((b) => ({
      faixa: `R$ ${b.low.toFixed(0)}–${b.high.toFixed(0)}`,
      qtd: b.qtd,
    }));
  }, [itens]);

  // Linha do tempo de preços (ordenado por data)
  const timelineData = useMemo(() => {
    return itens
      .map((it) => ({
        data: (it.data || it.data_publicacao || "").slice(0, 10),
        valor: parseFloat(it.valor || it.valor_unitario || 0),
        fonte: it.fonte || "?",
      }))
      .filter((x) => x.data && x.valor > 0)
      .sort((a, b) => a.data.localeCompare(b.data));
  }, [itens]);

  if (loading) return <div className="text-center py-20 text-[#94A3B8]">Carregando pesquisa...</div>;
  if (!pesq) return <div className="text-center py-20 text-[#94A3B8]">Pesquisa não encontrada.</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/pesquisas" className="inline-flex items-center gap-1 text-xs text-[#94A3B8] hover:text-[#F59E0B]" data-testid="back-link">
          <ArrowLeft size={12} /> Voltar para pesquisas
        </Link>
        <div className="flex items-start justify-between flex-wrap gap-4 mt-4">
          <div className="min-w-0">
            <div className="uppercase-label">Pesquisa nº {String(pesq.id).slice(0, 8).toUpperCase()}</div>
            <h1 className="font-heading text-3xl tracking-tight mt-2 text-[#FCD34D] break-words">
              {pesq.termo || "(sem termo)"}
            </h1>
            <p className="text-xs text-[#94A3B8] mt-2 max-w-3xl">
              <span className="text-[#F59E0B]">Contrato vinculado:</span>{" "}
              {contract ? (
                <Link to={`/contratos/${contract.id}`} className="text-[#FCD34D] hover:underline" data-testid="link-contrato">
                  {contract.numero}
                </Link>
              ) : <span>—</span>}
              {pesq.contract_item_numero ? <> · <strong>Item nº {pesq.contract_item_numero}</strong></> : null}
              {contract?.objeto ? <> · {contract.objeto.slice(0, 100)}</> : null}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge pesq={pesq} />
            <button
              data-testid="btn-download-pdf"
              onClick={baixarPdf}
              disabled={downloadingPdf}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-xs disabled:opacity-50"
            >
              <FilePdf size={14} weight="duotone" />
              {downloadingPdf ? "Baixando..." : "Gerar Relatório"}
            </button>
            <button
              data-testid="btn-export-xlsx"
              onClick={() => exportar("xlsx")}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-md border border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10 text-xs font-medium"
              title="Exportar Excel"
            >
              <FileXls size={14} weight="duotone" /> Excel
            </button>
            <button
              data-testid="btn-export-csv"
              onClick={() => exportar("csv")}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-md border border-[#3B82F6]/40 text-[#60A5FA] hover:bg-[#3B82F6]/10 text-xs font-medium"
              title="Exportar CSV"
            >
              <FileCsv size={14} weight="duotone" /> CSV
            </button>
            <button
              data-testid="btn-export-json"
              onClick={() => exportar("json")}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-md border border-[#94A3B8]/30 text-[#94A3B8] hover:bg-[#94A3B8]/10 text-xs font-medium"
              title="Exportar JSON"
            >
              <FileCode size={14} weight="duotone" /> JSON
            </button>
            <button
              data-testid="btn-excluir"
              onClick={excluir}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 text-xs font-medium"
            >
              <Trash size={14} /> Excluir
            </button>
          </div>
        </div>
      </div>

      {/* KPIs estilo M2A */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div data-testid="kpi-total-itens" className="tce-card p-4">
          <div className="uppercase-label">Total de itens</div>
          <div className="text-2xl font-mono mt-1 text-[#E9EEF5]">{itens.length}</div>
        </div>
        <div className="tce-card p-4">
          <div className="uppercase-label">Itens válidos</div>
          <div className="text-2xl font-mono mt-1 text-[#10B981]">{st.qtd || 0}</div>
          <div className="text-[10px] text-[#94A3B8]">{st.outliers_descartados || 0} outliers descartados</div>
        </div>
        <div className="tce-card p-4">
          <div className="uppercase-label">Período</div>
          <div className="text-xs font-mono mt-1 text-[#E9EEF5]">{pesq.data_inicio || "—"}</div>
          <div className="text-[10px] text-[#94A3B8]">até {pesq.data_fim || "—"}</div>
        </div>
        <div className="tce-card p-4">
          <div className="uppercase-label">CV</div>
          <div className="text-2xl font-mono mt-1 text-[#E9EEF5]">{st.cv ?? "—"}%</div>
          <div className="text-[10px] text-[#94A3B8]">{st.metodo_sugerido === "media" ? "Média aritmética" : "Menor preço"}</div>
        </div>
        <div data-testid="kpi-preco-referencia" className="tce-card p-4 border border-[#F59E0B]/40">
          <div className="uppercase-label">Preço de Referência</div>
          <div className="text-2xl font-mono mt-1 text-[#FCD34D]">{fmtBRL(st.preco_referencia)}</div>
          <div className="text-[10px] text-[#94A3B8]">Art. 6º IN 65/2021</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#334155] flex items-center gap-1 overflow-x-auto">
        {[
          { id: "itens", label: "Itens & Procedência", icon: Receipt },
          { id: "estat", label: "Estatísticas", icon: ChartBar },
          { id: "tecnica", label: "Nota técnica", icon: FileText },
          { id: "metadados", label: "Metadados", icon: CalendarBlank },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-medium uppercase tracking-wide border-b-2 transition-colors whitespace-nowrap ${
              tab === t.id ? "border-[#F59E0B] text-[#F59E0B]" : "border-transparent text-[#94A3B8] hover:text-[#E9EEF5]"
            }`}
          >
            <t.icon size={14} weight="duotone" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "itens" && (
        <div className="space-y-4">
          {/* Filtros */}
          <div className="tce-card p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="uppercase-label">Filtrar por</label>
                <input
                  data-testid="filtro-itens"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="Descrição, fornecedor ou número..."
                  className="mt-1 w-full px-3 py-2 bg-[#0B1220] border border-[#334155] rounded-md text-sm text-[#E9EEF5] placeholder:text-[#6B7280] focus:outline-none focus:border-[#F59E0B]/60"
                />
              </div>
              <div>
                <label className="uppercase-label">Fonte</label>
                <select
                  data-testid="filtro-fonte"
                  value={fonteFiltro}
                  onChange={(e) => setFonteFiltro(e.target.value)}
                  className="mt-1 w-full px-3 py-2 bg-[#0B1220] border border-[#334155] rounded-md text-sm text-[#E9EEF5] focus:outline-none focus:border-[#F59E0B]/60"
                >
                  <option value="">Todas ({itens.length})</option>
                  {fontesPresentes.map((f) => (
                    <option key={f} value={f}>{f} ({itens.filter((it) => it.fonte === f).length})</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="tce-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#0B1220] border-b border-[#334155]">
                  <tr className="text-left">
                    <th className="px-4 py-3 uppercase-label w-12">Item</th>
                    <th className="px-4 py-3 uppercase-label">Fonte</th>
                    <th className="px-4 py-3 uppercase-label">Descrição / Fornecedor</th>
                    <th className="px-4 py-3 uppercase-label">Órgão / Data</th>
                    <th className="px-4 py-3 uppercase-label text-right">V. Unitário</th>
                    <th className="px-4 py-3 uppercase-label text-center">Comprovação</th>
                  </tr>
                </thead>
                <tbody>
                  {itensFiltrados.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-[#94A3B8]">Nenhum item encontrado com este filtro.</td></tr>
                  )}
                  {itensFiltrados.map((it, idx) => {
                    const url = urlComprovacao(it);
                    return (
                      <tr key={idx} data-testid={`item-pesquisa-${idx}`} className="border-t border-[#334155] hover:bg-[#0B1220]/40">
                        <td className="px-4 py-3 font-mono text-xs text-[#94A3B8]">{String(idx + 1).padStart(2, "0")}</td>
                        <td className="px-4 py-3"><FonteBadge fonte={it.fonte} /></td>
                        <td className="px-4 py-3">
                          <div className="text-[#E9EEF5] text-xs leading-relaxed max-w-md">{(it.descricao_item || it.objeto || "—").slice(0, 200)}</div>
                          {it.fornecedor ? (
                            <div className="text-[10px] text-[#94A3B8] mt-1">Fornecedor: {it.fornecedor}</div>
                          ) : null}
                          {it.numero ? (
                            <div className="text-[10px] text-[#94A3B8]">Nº {it.numero}</div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs text-[#E9EEF5]">{it.orgao || it.municipio_nome || "—"}</div>
                          <div className="text-[10px] text-[#94A3B8]">{it.data || it.data_publicacao || "—"}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-[#FCD34D]">{fmtBRL(it.valor || it.valor_unitario)}</td>
                        <td className="px-4 py-3 text-center">
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer noopener"
                              data-testid={`url-comprovacao-${idx}`}
                              className="inline-flex items-center gap-1 text-xs text-[#005DA8] hover:underline"
                            >
                              Abrir fonte ↗
                            </a>
                          ) : <span className="text-[10px] text-[#94A3B8]">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "estat" && (
        <div className="space-y-4">
          <div className="tce-card p-6 space-y-4">
            <h2 className="font-heading text-xl text-[#F59E0B]">Análise Estatística (IN 65/2021)</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                ["Itens analisados", st.qtd ?? 0, ""],
                ["Outliers descartados (Art. 7º)", st.outliers_descartados ?? 0, ""],
                ["Menor valor", fmtBRL(st.menor), ""],
                ["Maior valor", fmtBRL(st.maior), ""],
                ["Média", fmtBRL(st.media), ""],
                ["Mediana", fmtBRL(st.mediana), ""],
                ["Coeficiente de Variação", `${st.cv ?? "—"}%`, st.cv > 25 ? "CV alto — usar menor preço" : "CV aceitável — usar média"],
                ["Método aplicado (Art. 6º)", st.metodo_sugerido === "media" ? "Média aritmética" : "Menor preço", ""],
              ].map(([label, val, sub]) => (
                <div key={label} className="bg-[#0B1220] rounded-md p-3 border border-[#334155]">
                  <div className="text-[10px] uppercase text-[#94A3B8] tracking-wide">{label}</div>
                  <div className="text-lg font-mono mt-1 text-[#E9EEF5]">{val}</div>
                  {sub ? <div className="text-[10px] text-[#94A3B8] mt-1">{sub}</div> : null}
                </div>
              ))}
            </div>
            <div className="border-t border-[#334155] pt-4">
              <div className="uppercase-label">Preço de Referência apurado</div>
              <div className="text-3xl font-mono mt-1 text-[#FCD34D]">{fmtBRL(st.preco_referencia)}</div>
            </div>
          </div>

          {/* Histograma de distribuição */}
          {histogramData.length > 0 && (
            <div className="tce-card p-6" data-testid="chart-histograma">
              <h3 className="font-heading text-base text-[#F59E0B] mb-3">Distribuição de preços</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={histogramData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                  <XAxis dataKey="faixa" stroke="#94A3B8" style={{ fontSize: 11 }} />
                  <YAxis stroke="#94A3B8" style={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid #334155", color: "#E9EEF5", fontSize: 12 }} />
                  <Bar dataKey="qtd" fill="#F59E0B" name="Itens nesta faixa" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Linha do tempo */}
          {timelineData.length > 1 && (
            <div className="tce-card p-6" data-testid="chart-timeline">
              <h3 className="font-heading text-base text-[#F59E0B] mb-3">Evolução dos preços (linha do tempo)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={timelineData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                  <XAxis dataKey="data" stroke="#94A3B8" style={{ fontSize: 11 }} />
                  <YAxis stroke="#94A3B8" style={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid #334155", color: "#E9EEF5", fontSize: 12 }} formatter={(v) => fmtBRL(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {st.preco_referencia ? (
                    <ReferenceLine y={st.preco_referencia} stroke="#10B981" strokeDasharray="5 4"
                                   label={{ value: `Ref. ${fmtBRL(st.preco_referencia)}`, fill: "#10B981", fontSize: 10, position: "insideTopRight" }} />
                  ) : null}
                  <Line type="monotone" dataKey="valor" stroke="#F59E0B" strokeWidth={2} dot={{ fill: "#FCD34D", r: 3 }} name="Valor unitário" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {tab === "tecnica" && (
        <div className="tce-card p-6 space-y-3">
          <h2 className="font-heading text-xl text-[#F59E0B]">Nota Técnica · Metodologia</h2>
          <p className="text-sm text-[#E9EEF5] leading-relaxed">
            A pesquisa de preços foi realizada conforme metodologia da{" "}
            <strong className="text-[#FCD34D]">{pesq.in_normativa || "IN SEGES/ME nº 65/2021"}</strong>,
            obtendo-se o preço de referência de{" "}
            <strong className="text-[#FCD34D]">{fmtBRL(st.preco_referencia)}</strong>{" "}
            pelo método de <strong className="text-[#FCD34D]">
              {st.metodo_sugerido === "media" ? "média aritmética" : "menor preço (CV {`>`} 25%)"}
            </strong>.
          </p>
          <ul className="text-xs text-[#94A3B8] space-y-1.5 list-disc list-inside pl-1">
            <li>Fontes consultadas: <span className="text-[#E9EEF5]">{(pesq.fontes || []).join(", ") || "—"}</span></li>
            <li>Modo de curadoria: <span className="text-[#E9EEF5]">{pesq.modo_curadoria === "manual" ? "Manual (usuário selecionou amostras)" : "Automática (todos os candidatos)"}</span></li>
            <li>Período de busca: <span className="text-[#E9EEF5]">{pesq.data_inicio} a {pesq.data_fim}</span></li>
            <li>Coeficiente de Variação: <span className="text-[#E9EEF5]">{st.cv ?? "—"}%</span></li>
            <li>Fiscal responsável: <span className="text-[#E9EEF5]">{pesq.fiscal_name} ({pesq.fiscal_email})</span></li>
          </ul>
        </div>
      )}

      {tab === "metadados" && (
        <div className="tce-card p-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {[
            ["ID da pesquisa", pesq.id],
            ["Criada em", fmtDate(pesq.created_at)],
            ["Termo", pesq.termo],
            ["Fiscal", `${pesq.fiscal_name || ""} (${pesq.fiscal_email || ""})`],
            ["Município", pesq.municipio_nome],
            ["Contrato vinculado", contract?.numero || "—"],
            ["Item do contrato", pesq.contract_item_numero ? `#${pesq.contract_item_numero}` : "—"],
            ["Modo de curadoria", pesq.modo_curadoria || "—"],
            ["Aditivo vinculado", pesq.aditivo_id || "—"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-start justify-between border-b border-[#334155] pb-2">
              <span className="text-[#94A3B8] text-xs uppercase tracking-wide">{k}</span>
              <span className="text-[#E9EEF5] text-xs text-right ml-3 break-all">{v || "—"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
